import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import type { Database } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/admin/quotes")({
  head: () => ({ meta: [{ title: "Admin · Package Quotes" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: QuotesPage,
});

type Line = { label: string; amount: number; kind: string };

const fmt = (n: number) => `$${Number(n).toLocaleString()}`;
const modeLabel: Record<string, string> = { web: "Website", leads: "Marketing", custom: "Custom" };

function QuotesPage() {
  type Row = Database["public"]["Tables"]["package_quotes"]["Row"];
  const [data, setData] = useState<Row[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from("package_quotes")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) setError(new Error(error.message));
    else setData(data);
    setLoading(false);
  }, []);
  useEffect(() => { void load(); }, [load]);
  const q = { data, error, isLoading, isError: !!error, refetch: load };

  return (
    <main className="mx-auto max-w-5xl px-5 py-10">
      <div className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-foreground">Package Quotes</h1>
        <Link to="/admin/leads" className="text-sm text-muted-foreground underline">All leads</Link>
      </div>

      {q.isLoading && <p className="text-muted-foreground">Loading…</p>}
      {q.isError && (
        <Card className="p-5">
          <p className="text-destructive">Couldn't load quotes: {(q.error as Error).message}</p>
          <Button className="mt-3" onClick={() => q.refetch()}>Retry</Button>
        </Card>
      )}
      {q.data && q.data.length === 0 && <p className="text-muted-foreground">No quotes yet.</p>}

      <div className="space-y-4">
        {q.data?.map((r) => {
          const lines = (r.line_items as unknown as Line[]) ?? [];
          return (
            <Card key={r.id} className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-foreground">
                    {r.business_name || "—"} <span className="font-normal text-muted-foreground">· {r.first_name} {r.last_name}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {r.email} · {r.phone}
                  </div>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                  {new Date(r.created_at).toLocaleString("en-US", { timeZone: "America/Denver" })}
                  <div className="mt-1 flex justify-end gap-1">
                    {r.builder_mode && <Badge variant="secondary">{modeLabel[r.builder_mode] ?? r.builder_mode}</Badge>}
                    {r.include_website && <Badge>Website · {r.pages} pages</Badge>}
                  </div>
                </div>
              </div>
              <ul className="mt-3 space-y-1 text-sm">
                {lines.map((l, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span className="text-foreground">{l.label}</span>
                    <span className="text-muted-foreground">
                      {l.amount < 0 ? "-" : ""}{fmt(Math.abs(l.amount))}{l.kind === "monthly" ? "/mo" : ""}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex flex-wrap gap-4 border-t border-border pt-3 text-sm font-semibold text-foreground">
                <span>One-time: {fmt(Number(r.one_time_total))}</span>
                <span>Monthly: {fmt(Number(r.monthly_total))}/mo</span>
                <span>To start: {fmt(Number(r.start_total))}</span>
              </div>
            </Card>
          );
        })}
      </div>
    </main>
  );
}
